import { useTriggerEventContext } from '@components/utils/contexts/TriggerEventContext';
import { TrackEvent } from '@enums';
import { Enum_Modals_Trigger } from '@graphql/generated/graphql';
import { useAnalytics } from '@lib/analytics/analyticsContext';
import { AnimationControls } from 'framer-motion';
import { useRouter } from 'next/router';
import {
    createContext,
    Dispatch,
    FC,
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useReducer,
} from 'react';
import { useCounter } from 'react-use';

import { useNavigationContext } from './NavigationContext';
import { getValidPageIndex } from '../utils/getValidPageIndex';

interface ProgressContext {
    isPaused: boolean;
    numberOfPages: number;
    currentPageIndex: number;
    animationControls?: AnimationControls;
    pageDuration?: number;
    startAnimation?: (duration?: number, isInfinite?: boolean) => void;
    pauseAnimation?: () => void;
    resumeAnimation?: () => void;
    resetAnimation?: () => void;
    loopCountInc: () => void;
}

export interface ProgressContextProps extends PropsWithChildren {
    numberOfPages: number;
    pageIndex?: number;
}

type ProgressAction =
    | { type: 'START'; payload?: { duration: number; isInfinite?: boolean } }
    | { type: 'PAUSE' }
    | { type: 'RESUME' }
    | { type: 'RESET' }
    | { type: 'RESTART' }
    | { type: 'SET_PAGE'; payload: { number: number } }
    | { type: 'PREVIOUS_PAGE' }
    | { type: 'NEXT_PAGE' }
    | {
          type: 'SET_ANIMATIONS';
          payload: {
              startAnimation: (duration?: number, isInfinite?: boolean) => void;
              pauseAnimation: () => void;
              resumeAnimation: () => void;
              resetAnimation: () => void;
          };
      };

const partialInitialContext = {
    isPaused: false,
    numberOfPages: 0,
    currentPageIndex: 0,
    isMuted: false,
    loopCountInc: () => {},
};

const progressReducer = (state: ProgressContext, action: ProgressAction) => {
    switch (action.type) {
        case 'START':
            state.startAnimation?.(
                action.payload?.duration,
                action.payload?.isInfinite
            );
            return {
                ...state,
                isPaused: false,
                pageDuration: action.payload?.duration,
            };

        case 'PAUSE':
            state.pauseAnimation?.();
            return { ...state, isPaused: true };

        case 'RESUME':
            state.resumeAnimation?.();
            return { ...state, isPaused: false };

        case 'RESET': {
            state.resetAnimation?.();
            return state;
        }

        case 'RESTART': {
            state.loopCountInc();
            return state;
        }

        case 'SET_PAGE':
            return {
                ...state,
                currentPageIndex: action.payload.number,
            };

        case 'PREVIOUS_PAGE':
            return {
                ...state,
                currentPageIndex: Math.max(state.currentPageIndex - 1, 0),
            };

        case 'NEXT_PAGE':
            return {
                ...state,
                currentPageIndex: Math.min(
                    state.currentPageIndex + 1,
                    state.numberOfPages - 1
                ),
            };

        case 'SET_ANIMATIONS':
            return {
                ...state,
                startAnimation: action.payload.startAnimation,
                pauseAnimation: action.payload.pauseAnimation,
                resumeAnimation: action.payload.resumeAnimation,
                resetAnimation: action.payload.resetAnimation,
            };

        default:
            return state;
    }
};

export const ProgressContext = createContext<ProgressContext>(
    partialInitialContext
);

export const ProgressDispatchContext = createContext<Dispatch<ProgressAction>>(
    () => {}
);

export const ProgressProvider: FC<ProgressContextProps> = ({
    children,
    numberOfPages,
    pageIndex,
}) => {
    const { track } = useAnalytics();
    const { onTriggerEvent } = useTriggerEventContext();
    const router = useRouter();
    const { storyPages, story } = useNavigationContext();

    const [, { inc: loopCountInc, reset: loopCountReset, get: loopCountGet }] =
        useCounter(0);

    const initialContext: ProgressContext = {
        ...partialInitialContext,
        numberOfPages,
        loopCountInc,
    };

    const [state, dispatch] = useReducer(progressReducer, {
        ...initialContext,
    });

    useEffect(() => {
        if (pageIndex) {
            dispatch({
                type: 'SET_PAGE',
                payload: {
                    number: getValidPageIndex({
                        numberOfPages,
                        index: pageIndex,
                    }),
                },
            });
        }
    }, [numberOfPages, pageIndex]);

    const handleRouteChange = useCallback(
        (targetUrl: string) => {
            if (
                router.asPath.includes('onboarding') &&
                !targetUrl.includes('onboarding')
            ) {
                track(TrackEvent.ExitOnboarding, {
                    current_step: state.currentPageIndex + 1,
                    total_steps: state.numberOfPages,
                });
            }
        },
        [router.asPath, state.currentPageIndex, state.numberOfPages, track]
    );

    useEffect(() => {
        if (!story) return;

        onTriggerEvent({
            event: Enum_Modals_Trigger.DisplayStoryPage,
            filters: {
                title: storyPages[state.currentPageIndex].subtitle,
                storySlug: story.Slug,
            },
        });

        if (state.currentPageIndex == state.numberOfPages - 1) {
            onTriggerEvent({
                event: Enum_Modals_Trigger.LastStoryPage,
                filters: { storySlug: story.Slug },
            });
        }

        if (state.currentPageIndex == 0) {
            onTriggerEvent({
                event: Enum_Modals_Trigger.FirstStoryPage,
                filters: { storySlug: story.Slug },
            });
        }

        const t_init = performance.now();

        router.events.on('routeChangeStart', handleRouteChange);

        return () => {
            // only send event when user pass
            track(TrackEvent.StoryPageView, {
                story_title: story.Title,
                index: state.currentPageIndex,
                page_title: storyPages[state.currentPageIndex].subtitle,
                total_pages: numberOfPages,
                video_duration: state.pageDuration || 0,
                view_duration: Math.round(performance.now() - t_init),
                loop_count: loopCountGet(),
            });
            loopCountReset();
            router.events.off('routeChangeStart', handleRouteChange);
        };
    }, [
        numberOfPages,
        router.events,
        state.currentPageIndex,
        state.numberOfPages,
        state.pageDuration,
        story,
        storyPages,
        handleRouteChange,
        loopCountGet,
        loopCountReset,
        onTriggerEvent,
        track,
    ]);

    return (
        <ProgressContext.Provider value={state}>
            <ProgressDispatchContext.Provider value={dispatch}>
                {children}
            </ProgressDispatchContext.Provider>
        </ProgressContext.Provider>
    );
};

export const useProgressContext = () => useContext(ProgressContext);

export const useProgressDispatchContext = () =>
    useContext(ProgressDispatchContext);
